import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../../common/components/Icon/Icon";
import { TransformedOrder } from "../types/orderApiTypes";

interface OrderHeaderProps {
  order: TransformedOrder;
  isHistoryView?: boolean;
}

const OrderHeader: React.FC<OrderHeaderProps> = ({
  order,
  isHistoryView = false,
}) => {
  const { t } = useTranslation();
  const { instructions } = order.paymentMethodInfo;
  const { status } = order;

  const baseClass = "order-page__header";
  const modifierClass = isHistoryView ? `${baseClass}--history` : "";

  // --- 1. Dynamic Title Logic ---
  const getTitle = () => {
    if (isHistoryView) {
      return t("orderPage.detailsTitle", "Order Details");
    }

    // Context: Checkout Success Page
    if (["failed", "cancelled", "refunded"].includes(status)) {
      return t("orderPage.failedTitle", "Order Unsuccessful");
    }

    return t("orderPage.successTitle", "Thank You! Your Order is Confirmed.");
  };

  // --- 2. Dynamic Message Logic ---
  const getOrderMessage = () => {
    // Priority 1: Negative Statuses (Override instructions)
    // If an order is cancelled or failed, we shouldn't show "Please pay to IBAN..." instructions.
    if (status === "failed") {
      return t(
        "orderPage.messages.failed",
        "Payment failed. Please try again or contact support."
      );
    }
    if (status === "cancelled") {
      return t(
        "orderPage.messages.cancelled",
        "This order has been cancelled."
      );
    }
    if (status === "refunded") {
      return t(
        "orderPage.messages.refunded",
        "This order has been fully refunded."
      );
    }

    // Priority 2: Payment Gateway Instructions
    // e.g., Bank Transfer details. Valid for on-hold, pending, or processing.
    if (instructions) {
      return instructions;
    }

    // Priority 3: Context-Specific Status Messages
    if (isHistoryView) {
      // --- History View Messages ---
      switch (status) {
        case "completed":
          return t(
            "orderPage.messages.history.completed",
            "This order has been completed and delivered."
          );
        case "processing":
          return t(
            "orderPage.messages.history.processing",
            "We are currently preparing your order."
          );
        case "on-hold":
          return t(
            "orderPage.messages.history.onHold",
            "We are waiting for payment confirmation."
          );
        case "pending":
          return t("orderPage.messages.history.pending", "Payment is pending.");
        default:
          return t(
            "orderPage.messages.history.default",
            "Order details are listed below."
          );
      }
    } else {
      // --- Checkout Success View Messages ---
      switch (status) {
        case "completed":
        case "processing":
          return t(
            "orderPage.successMessage",
            "You will receive an email confirmation with your order details shortly."
          );
        case "on-hold":
          return t(
            "orderPage.messages.checkout.onHold",
            "Your order is on hold. We will process it once payment is confirmed."
          );
        case "pending":
          return t(
            "orderPage.messages.checkout.pending",
            "Your order is pending payment."
          );
        default:
          return t(
            "orderPage.successMessage",
            "You will receive an email confirmation with your order details shortly."
          );
      }
    }
  };

  // --- 3. Dynamic Icon Logic ---
  const getIcon = () => {
    if (isHistoryView) return null;

    if (["failed", "cancelled"].includes(status)) {
      return (
        <Icon
          iconName="x-mark"
          className="icon--stroke-thicker"
          style={{ color: "var(--color-danger)" }}
        />
      );
    }

    if (["on-hold", "pending"].includes(status)) {
      return (
        <Icon
          iconName="clock"
          className="icon--stroke-thicker"
          style={{ color: "var(--color-warning)" }}
        />
      );
    }

    // Default Success Checkmark
    return <Icon iconName="check" className="icon--stroke-thicker" />;
  };

  const messageHtml = getOrderMessage();

  return (
    <div className={`${baseClass} ${modifierClass}`}>
      {getIcon()}

      <h1 className="order-page__title">{getTitle()}</h1>

      <div className="order-page__status-wrapper">
        <strong className="order-page__status-label">
          {t("orderPage.statusLabel", "Order Status")}:
        </strong>
        <span
          className={`order-page__status-badge order-page__status-badge--${order.status}`}
        >
          {t(`orderPage.status.${order.status}`, order.status)}
        </span>
      </div>

      <div
        className="order-page__subtitle"
        dangerouslySetInnerHTML={{
          __html: messageHtml,
        }}
      />
    </div>
  );
};

export default OrderHeader;
